import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
  activateAdmin,
  closeRaid,
  createRaid,
  getFavorites,
  getRaid,
  getRaids,
  markResourceReceived,
  postMelangePreview,
  recoverAdminPin,
  startRaid,
  startTimeTracking,
  stopTimeTracking,
  updateRaid,
} from "./RaidApi.ts";
import type {
  AdminAccess,
  CreatedRaid,
  MelangeDistributionResult,
  MelangePreviewRequest,
  Participant,
  Raid,
} from "./RaidSchema.ts";
import a from "axios";
import {ensureCsrf} from "@/api/csrfService.ts";
import type {GeneralError} from "@/api/Types.ts";

type UseChangedRaidOptions = GeneralError & {
  onSuccess: (raid: Raid) => void;
};

type UseCreatedRaidOptions = GeneralError & {
  onSuccess: (created: CreatedRaid) => void;
};

type UseAdminAccessOptions = GeneralError & {
  onSuccess: (access: AdminAccess) => void;
};

type MarkResourceReceivedOptions = GeneralError & {
  onSuccess: (participant: number) => void;
};

type MelangePreviewMutationOptions = GeneralError & {
  onSuccess: (result: MelangeDistributionResult) => void;
};

type MelangePreviewVariables = {
  raidId: Raid["id"];
  payload: MelangePreviewRequest;
};

export const useHomeRaidData = () =>
    useQuery({
      queryKey: ["homedata"],
      queryFn: ({ signal }) => getFavorites(signal),
    });

const useGetRaidList = () =>
    useQuery({
      queryKey: ["raids"],
      queryFn: ({ signal }) => getRaids(signal),
    });

const useGetRaid = (id: number, enabled: boolean, poll = false) =>
    useQuery({
      queryKey: ["raid", id],
      queryFn: ({ signal }) => getRaid(id, signal),
      retry: 1,
      enabled,
      refetchInterval: poll ? 15_000 : false,
    });

const useCreateRaid = ({onSuccess, onError}: UseCreatedRaidOptions) => {
  const queryClient = useQueryClient();

  return useMutation<CreatedRaid, unknown, string>({
    mutationFn: (name) => createRaid(name),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({queryKey: ["raids"]});
      onSuccess(data);
    },
    onError: (error) => {
      if (a.isAxiosError(error)) {
        console.error("Server Error:", error.status, error.response?.data);
        onError?.(error.status, error.response?.data);
      } else {
        console.error("Validation Error:", error);
        onError?.(0, "Unknown error");
      }
    },
  });
};

const useActivateAdmin = ({onSuccess, onError}: UseAdminAccessOptions) =>
    useMutation({
      mutationFn: async ({raidId, pin}: { raidId: number; pin: string }) => {
        await ensureCsrf();
        return activateAdmin(raidId, pin);
      },
      onSuccess: (data) => onSuccess(data),
      onError: (error) => {
        if (a.isAxiosError(error)) {
          console.error("Server Error:", error.response?.data);
          onError?.(error.status ?? 0, error.response?.data);
        } else {
          console.error("Validation Error:", error);
          onError?.(0, error instanceof Error ? error.message : "Unknown error");
        }
      },
    });

const useRecoverAdminPin = ({onSuccess, onError}: UseAdminAccessOptions) =>
    useMutation({
      mutationFn: (raidId: number) => recoverAdminPin(raidId),
      onSuccess: (data) => onSuccess(data),
      onError: (error) => {
        if (a.isAxiosError(error)) {
          console.error("Server Error:", error.response?.data);
          onError?.(error.status ?? 0, error.response?.data);
        } else {
          console.error("Validation Error:", error);
          onError?.(0, error instanceof Error ? error.message : "Unknown error");
        }
      },
    });

type UpdateRaidPayload = {
  id: Raid["id"];
  payload: Partial<Raid>;
};

const useUpdateRaid = (
    {onError, onSuccess}: UseChangedRaidOptions,
) => {
  // const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id, payload}: UpdateRaidPayload) => {
      await ensureCsrf();
      return updateRaid(id, payload);
    },
    onSuccess: (data: Raid) => {

      // void queryClient.invalidateQueries({queryKey: ["raid", variables.id]});
      onSuccess(data);
    },
    onError: (error) => {
      if (a.isAxiosError(error)) {
        console.error("Server Error:", error.response?.data);
        onError?.(error.status ?? 0, error.response?.data);
      } else {
        console.error("Validation Error:", error);
        onError?.(0, error.message);
      }
    },
  });
};

type TimeTrackParticipant = {
  raidId: Raid["id"];
  participantId: Participant["id"];
};

const useStopTimeTrackParticipant = (
    {onError, onSuccess}: UseChangedRaidOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({raidId, participantId}: TimeTrackParticipant) => {
      await ensureCsrf();
      return stopTimeTracking(raidId, participantId);
    },
    onSuccess: (data: Raid) => {
      queryClient.setQueryData(["raid", data.id], data);
      onSuccess(data);
    },
    onError: (error) => {
      if (a.isAxiosError(error)) {
        console.error("Server Error:", error.response?.data);
        onError?.(error.status ?? 0, error.response?.data);
      } else {
        console.error("Validation Error:", error);
        onError?.(0, error.message);
      }
    },
  });
};

const useStartTimeTrackParticipant = (
    {onError, onSuccess}: UseChangedRaidOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({raidId, participantId}: TimeTrackParticipant) => {
      await ensureCsrf();
      return startTimeTracking(raidId, participantId);
    },
    onSuccess: (data: Raid) => {
      queryClient.setQueryData(["raid", data.id], data);
      onSuccess(data);
    },
    onError: (error) => {
      if (a.isAxiosError(error)) {
        console.error("Server Error:", error.response?.data);
        onError?.(error.status ?? 0, error.response?.data);
      } else {
        console.error("Validation Error:", error);
        onError?.(0, error.message);
      }
    },
  });
};

type ChangeStatusRaid = {
  id: Raid["id"];
  payload: Raid;
};

const useStartRaid = ({onError, onSuccess}: UseChangedRaidOptions) => {
  // const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id, payload}: ChangeStatusRaid) => {
      await ensureCsrf();
      return startRaid(id, payload);
    },
    onSuccess: (data: Raid) => {
      console.log("Raid gestartet!");
      // void queryClient.invalidateQueries({queryKey: ["raid", variables.id]});
      onSuccess(data);
    },
    onError: (error) => {
      if (a.isAxiosError(error)) {
        console.error("Server Error:", error.response?.data);
        onError?.(error.status, error.response?.data);
      } else {
        console.error("Validation Error:", error);
      }
    },
  });
};

const useStopRaid = ({onError, onSuccess}: UseChangedRaidOptions) => {
  // const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id, payload}: ChangeStatusRaid) => {
      await ensureCsrf();
      return closeRaid(id, payload);
    },
    onSuccess: (data: Raid) => {
      console.log("Raid geschlossen!");
      // void queryClient.invalidateQueries({queryKey: ["raid", variables.id]});
      onSuccess(data);
    },
    onError: (error) => {
      if (a.isAxiosError(error)) {
        console.error("Server Error:", error.response?.data);
        onError?.(error.status, error.response?.data);
      } else {
        console.error("Validation Error:", error);
      }
    },
  });
};

const useMelangePreview = ({onError, onSuccess}: MelangePreviewMutationOptions) =>
    useMutation({
      mutationFn: async ({raidId, payload}: MelangePreviewVariables) => {
        await ensureCsrf();
        return postMelangePreview(raidId, payload);
      },
      onSuccess: (data) => {
        onSuccess(data);
      },
      onError: (error) => {
        if (a.isAxiosError(error)) {
          console.error("Melange preview error:", error.response?.data);
          onError?.(error.status ?? 0, error.response?.data);
        } else {
          console.error("Melange preview validation error:", error);
          onError?.(0, error instanceof Error ? error.message : "Unknown error");
        }
      },
    });

export function useMarkResourceReceived({onError, onSuccess}: MarkResourceReceivedOptions) {
  return useMutation({
    mutationFn: async (participantId: number) => {
      await ensureCsrf();
      await markResourceReceived(participantId);
      return participantId;
    },
    onSuccess: (participantId : number) => {
      onSuccess?.(participantId);
    },
    onError: (err) => {
      if (a.isAxiosError(err)) {
        const status = err.response?.status;
        onError?.(status, undefined);
      }
      onError?.(undefined, "Unknown network error")
    },
  });
}

export {
  useCreateRaid,
  useGetRaidList,
  useGetRaid,
  useActivateAdmin,
  useRecoverAdminPin,
  useUpdateRaid,
  useStartRaid,
  useStopRaid,
  useMelangePreview,
  useStopTimeTrackParticipant,
  useStartTimeTrackParticipant,
};
